import { Alert, CircularProgress, MenuItem, Stack, TextField } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { RecordFormLayout } from '@/components/RecordFormLayout';
import { getPeople, type Person } from '@/features/people/api/people';
import {
  getOneOffStops,
  getRouteAssignments,
  saveOneOffStop,
  saveRouteAssignment,
  type RouteAssignment,
} from '@/features/service-locations/api/serviceLocations';

type FormState = {
  userId: number;
  startDate: string;
  endDate: string;
  frequency: string;
  notes: string;
};

export function ScheduleFormPage({ kind }: { kind: 'recurring' | 'one-off' }) {
  const params = useParams();
  const clientId = Number(params.clientId);
  const locationId = Number(params.locationId);
  const rawId = kind === 'recurring' ? params.assignmentId : params.visitId;
  const isNew = rawId === 'new';
  const recordId = isNew ? null : Number(rawId);
  const technicians = useQuery({ queryKey: ['people', 'technicians'], queryFn: () => getPeople('technicians') });
  const records = useQuery({
    queryKey: ['location', clientId, locationId, kind === 'recurring' ? 'assignments' : 'one-off-stops'],
    queryFn: () => kind === 'recurring' ? getRouteAssignments(clientId, locationId) : getOneOffStops(clientId, locationId),
  });
  const assignableTechnicians = useMemo(
    () => (technicians.data ?? []).filter((technician) => technician.name.trim().toLowerCase() !== 'test technician'),
    [technicians.data],
  );

  if (records.isLoading || technicians.isLoading) return <Stack alignItems="center" sx={{ p: 8 }}><CircularProgress /></Stack>;
  const record = recordId === null ? null : records.data?.find((item) => item.id === recordId);
  if (!isNew && !record) return <Alert severity="error" sx={{ m: 2 }}>This schedule record could not be loaded.</Alert>;

  const initial = record ? mapRecord(record) : emptyForm(assignableTechnicians[0]?.id ?? 0);
  return (
    <ScheduleFormEditor
      key={`${kind}-${recordId ?? 'new'}`}
      kind={kind}
      clientId={clientId}
      locationId={locationId}
      recordId={recordId}
      technicians={assignableTechnicians}
      initial={initial}
    />
  );
}

function ScheduleFormEditor({
  kind,
  clientId,
  locationId,
  recordId,
  technicians,
  initial,
}: {
  kind: 'recurring' | 'one-off';
  clientId: number;
  locationId: number;
  recordId: number | null;
  technicians: Person[];
  initial: FormState;
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const back = () => { void navigate(`/clients/${clientId}/service-locations/${locationId}?tab=schedule`); };
  const save = useMutation({
    mutationFn: () =>
      kind === 'recurring'
        ? saveRouteAssignment(
            clientId,
            locationId,
            { userId: form.userId, startDate: form.startDate, endDate: form.endDate || null, frequency: form.frequency },
            recordId ?? undefined,
          )
        : saveOneOffStop(clientId, locationId, form.userId, form.startDate, recordId ?? undefined, form.notes),
    onSuccess: back,
  });
  const type = kind === 'recurring' ? 'Route Assignment' : 'One-off Visit';

  return (
    <RecordFormLayout
      title={recordId === null ? `New ${type}` : type}
      type={type}
      saving={save.isPending}
      disabled={!form.userId || !form.startDate}
      dirty={JSON.stringify(form) !== JSON.stringify(initial)}
      error={save.isError}
      onBack={back}
      onSave={() => save.mutate()}
      onSaveAndClose={() => save.mutate()}
    >
      <Stack spacing={2}>
        <TextField select required label="Technician" value={form.userId} onChange={(event) => setForm({ ...form, userId: Number(event.target.value) })}>
          {technicians.map((technician) => <MenuItem key={technician.id} value={technician.id}>{technician.name}</MenuItem>)}
        </TextField>
        {!technicians.length && <Alert severity="warning">Add a technician before creating this record.</Alert>}
        <TextField type="date" required label={kind === 'recurring' ? 'Start date' : 'Visit date'} slotProps={{ inputLabel: { shrink: true } }} value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} />
        {kind === 'recurring' ? (
          <>
            <TextField type="date" label="End date (optional)" slotProps={{ inputLabel: { shrink: true } }} value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} />
            <TextField select label="Frequency" value={form.frequency} onChange={(event) => setForm({ ...form, frequency: event.target.value })}>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="fortnightly">Fortnightly</MenuItem>
              <MenuItem value="three-weekly">Every three weeks</MenuItem>
              <MenuItem value="four-weekly">Every four weeks</MenuItem>
            </TextField>
          </>
        ) : (
          <TextField multiline minRows={5} label="Notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} helperText="This note is also recorded on the client timeline." />
        )}
      </Stack>
    </RecordFormLayout>
  );
}

const mapRecord = (record: RouteAssignment): FormState => ({
  userId: record.user_id,
  startDate: record.start_date.slice(0, 10),
  endDate: record.end_date?.slice(0, 10) ?? '',
  frequency: record.frequency,
  notes: record.notes ?? '',
});
const emptyForm = (userId: number): FormState => ({
  userId,
  startDate: localDate(new Date()),
  endDate: '',
  frequency: 'weekly',
  notes: '',
});
const localDate = (value: Date) => {
  const offset = value.getTimezoneOffset();
  return new Date(value.getTime() - offset * 60_000).toISOString().slice(0, 10);
};